import React, { useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import Animated, { FadeInDown, FadeIn, FadeOut } from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import { isWeb } from '@/lib/platform';
import { useRealtimeNotifications, useAuth } from '@/lib/supabase';
import {
  Users2,
  Rocket,
  TrendingUp,
  MessageSquare,
  Shield,
  Activity,
  ArrowUpRight,
  AlertCircle,
  Bell,
  Bug,
  BarChart3,
  Zap,
  FolderOpen,
  Clock,
  Flag,
  Timer,
  UserX,
  DollarSign,
  Gauge,
  ChevronRight,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import {
  useAdminRole,
  useAdminMetricsV2,
  useAdminActivityLog,
  ADMIN_ROLE_LABELS,
  hasPermission,
} from '@/lib/admin';
import { hapticLight } from '@/lib/haptics';
import { AppLayout } from '@/components/AppLayout';
import { WebCard, WebGrid } from '@/components/web/WebAppShell';
import { StatCardEnhanced } from '@/components/admin/StatCardEnhanced';
import { MiniBarChart } from '@/components/admin/MiniBarChart';
import { QuickActions } from '@/components/admin/QuickActions';
import { AdminActivityLog } from '@/components/admin/AdminActivityLog';
import type { FeedbackStatus, AdminSubRole, AdminDashboardMetricsV2, AdminActivityLogEntry } from '@/lib/admin';
import { isBetaMode } from '@/lib/config/app-mode';
import { useWaitlistStats, type WaitlistStats } from '@/lib/admin/use-waitlist-stats';

const STATUS_COLORS: Record<FeedbackStatus, string> = {
  new: '#00B8B8',
  in_review: '#FF9500',
  resolved: '#34C759',
  closed: '#8E8E93',
};

const STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'New',
  in_review: 'In Review',
  resolved: 'Resolved',
  closed: 'Closed',
};

export default function AdminDashboardScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  // Unread count for the mobile bell below — the admin shell had no
  // notification surface at all.
  const { user: authUser } = useAuth();
  const { unreadCount } = useRealtimeNotifications(authUser?.id);

  const { data: adminRole, isLoading: roleLoading } = useAdminRole();
  const {
    data: metrics,
    isLoading: metricsLoading,
    isError: metricsError,
    refetch: refetchMetrics,
  } = useAdminMetricsV2();
  const { data: activityLog } = useAdminActivityLog(5);
  // Beta-only: DELETE this line + waitlistStats prop when beta ends
  const { data: waitlistStats } = useWaitlistStats();

  if (roleLoading || metricsLoading) {
    return (
      <AppLayout
        title="Admin"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Dashboard' }]}
      >
        <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
          <ActivityIndicator size="large" color={colors.primary[600]} />
        </View>
      </AppLayout>
    );
  }

  // A failed metrics query must NOT render as a zero-filled dashboard (that
  // reads as "0 users / 0 startups" — an ops trap). Surface the error + retry
  // instead. A genuine all-zero dashboard only shows once the query succeeds.
  if (metricsError || !metrics) {
    return (
      <AppLayout
        title="Admin"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Dashboard' }]}
      >
        <View className="flex-1 items-center justify-center px-8" style={{ backgroundColor: colors.surface.secondary }}>
          <AlertCircle size={32} color={colors.error} />
          <Text style={{ color: colors.text.primary }} className="text-base font-semibold mt-3 text-center">
            Couldn&apos;t load admin metrics
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm mt-1 mb-4 text-center">
            The metrics query failed. This is not the same as zero activity.
          </Text>
          <AnimatedPressable
            onPress={() => refetchMetrics()}
            className="px-5 py-2.5 rounded-xl"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text style={{ color: '#fff' }} className="font-semibold">Retry</Text>
          </AnimatedPressable>
        </View>
      </AppLayout>
    );
  }

  const canSeeEngagement = hasPermission(adminRole, 'dashboard_engagement');
  const canSeeActivityLog = hasPermission(adminRole, 'dashboard_activity_log');
  const canSeeFeedback = hasPermission(adminRole, 'feedback');
  const isSupport = adminRole === 'support' || adminRole === 'super_admin';
  const isAnalyst = adminRole === 'analyst' || adminRole === 'super_admin';

  const feedbackByStatus = metrics?.feedbackByStatus ?? { new: 0, in_review: 0, resolved: 0, closed: 0 };

  if (!isWeb) {
    return (
      <MobileDashboard
        adminRole={adminRole}
        metrics={metrics}
        activityLog={activityLog}
        canSeeFeedback={canSeeFeedback}
        canSeeEngagement={canSeeEngagement}
        canSeeActivityLog={canSeeActivityLog}
        isSupport={isSupport}
        isAnalyst={isAnalyst}
        feedbackByStatus={feedbackByStatus}
        waitlistStats={waitlistStats}
        router={router}
        colors={colors}
      />
    );
  }

  const maxStatusCount = Math.max(...Object.values(feedbackByStatus), 1);

  return (
    <AppLayout
      title="Dashboard"
      breadcrumbs={[{ label: 'Admin' }, { label: 'Dashboard' }]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['bottom']}>
        <ScrollView className="flex-1" showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 32 }}>
          {/* Welcome banner */}
          <Animated.View entering={FadeInDown.delay(100).duration(500)} className="mb-6">
            <WebCard>
              <View className="flex-row items-center justify-between">
                <View className="flex-1">
                  <View className="flex-row items-center gap-2 mb-1">
                    <Shield size={16} color={colors.primary[600]} />
                    <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
                      {adminRole ? ADMIN_ROLE_LABELS[adminRole] : 'Admin'}
                    </Text>
                  </View>
                  <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                    Admin Dashboard
                  </Text>
                  <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
                    Platform overview and analytics
                  </Text>
                </View>
                <View className="flex-row items-center gap-2 shrink-0">
                  {/*
                    Bell — MOBILE ONLY, mirroring the investor and founder
                    dashboards. `AppLayout` only renders `WebAppShell` (which
                    owns the bell) on web desktop/tablet, so an admin on a phone
                    had NO route to /notifications at all: notifications were
                    being written for admins and were unreachable. Same gap that
                    was already closed for the other two personas.
                  */}
                  {!isWeb && (
                    <AnimatedPressable
                      onPress={() => router.push('/notifications')}
                      accessibilityRole="button"
                      accessibilityLabel={
                        unreadCount > 0
                          ? `Notifications, ${unreadCount} unread`
                          : 'Notifications'
                      }
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 22,
                        backgroundColor: colors.surface.secondary,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Bell size={20} color={colors.text.secondary} />
                      {unreadCount > 0 && (
                        <View
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            minWidth: 8,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: colors.error,
                          }}
                        />
                      )}
                    </AnimatedPressable>
                  )}
                  <View
                    className="w-12 h-12 rounded-2xl items-center justify-center"
                    style={{ backgroundColor: `${colors.primary[600]}12` }}
                  >
                    <Activity size={24} color={colors.primary[600]} />
                  </View>
                </View>
              </View>
            </WebCard>
          </Animated.View>

          {/* Primary KPI Grid */}
          <Animated.View entering={FadeInDown.delay(200).duration(500)} className="mb-6">
            <WebGrid columns={{ mobile: 2, tablet: 2, desktop: 4 }} gap={16}>
              <StatCardEnhanced
                icon={<Users2 size={22} color={colors.primary[600]} />}
                iconBg={`${colors.primary[600]}12`}
                label="Total Users"
                value={metrics?.totalUsers ?? 0}
                delta={metrics?.usersDelta}
                deltaLabel="vs last week"
              />
              <StatCardEnhanced
                icon={<Rocket size={22} color="#34C759" />}
                iconBg="#34C75912"
                label="Startups"
                value={metrics?.totalStartups ?? 0}
                delta={metrics?.startupsDelta}
                deltaLabel="vs last week"
              />
              <StatCardEnhanced
                icon={<TrendingUp size={22} color="#007AFF" />}
                iconBg="#007AFF12"
                label="Investors"
                value={metrics?.totalInvestors ?? 0}
                delta={metrics?.investorsDelta}
                deltaLabel="vs last week"
              />
              <StatCardEnhanced
                icon={<MessageSquare size={22} color="#FF9500" />}
                iconBg="#FF950012"
                label="Feedback"
                value={metrics?.totalFeedback ?? 0}
                delta={metrics?.feedbackDelta}
                deltaLabel="vs last week"
                onPress={() => {
                  hapticLight();
                  router.push('/(admin)/feedback');
                }}
              />
            </WebGrid>
          </Animated.View>

          {/* Secondary KPI Row (role-gated) */}
          {(isSupport || isAnalyst) && (
            <Animated.View entering={FadeInDown.delay(250).duration(500)} className="mb-6">
              <WebGrid columns={{ mobile: 2, tablet: 2, desktop: 4 }} gap={16}>
                {isSupport && (
                  <>
                    <StatCardEnhanced
                      icon={<AlertCircle size={22} color="#FF9500" />}
                      iconBg="#FF950012"
                      label="Open Feedback"
                      value={metrics?.openFeedbackCount ?? 0}
                      onPress={() => {
                        hapticLight();
                        router.push('/(admin)/feedback');
                      }}
                    />
                    <StatCardEnhanced
                      icon={<Bug size={22} color="#FF3B30" />}
                      iconBg="#FF3B3012"
                      label="Critical Bugs"
                      value={metrics?.criticalBugs ?? 0}
                    />
                  </>
                )}
                {isAnalyst && (
                  <>
                    <StatCardEnhanced
                      icon={<BarChart3 size={22} color={colors.primary[600]} />}
                      iconBg={`${colors.primary[600]}12`}
                      label="Avg Inverio"
                      value={metrics?.avgScore ?? 0}
                    />
                    <StatCardEnhanced
                      icon={<Zap size={22} color="#34C759" />}
                      iconBg="#34C75912"
                      label="Active User Rate"
                      value={metrics?.dau && metrics?.totalUsers
                        ? `${Math.round((metrics.dau / metrics.totalUsers) * 100)}%`
                        : '0%'}
                    />
                  </>
                )}
              </WebGrid>
            </Animated.View>
          )}

          {/* Charts row: Signup Trend + Feedback by Status */}
          <Animated.View entering={FadeInDown.delay(300).duration(500)} className="mb-6">
            <WebGrid columns={{ mobile: 1, tablet: 1, desktop: 2 }} gap={16}>
              {/* Signup Trend */}
              <WebCard>
                <View className="flex-row items-center justify-between mb-4">
                  <View className="flex-row items-center gap-3">
                    <View
                      className="w-10 h-10 rounded-xl items-center justify-center"
                      style={{ backgroundColor: `${colors.primary[600]}12` }}
                    >
                      <TrendingUp size={20} color={colors.primary[600]} />
                    </View>
                    <View>
                      <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                        Signup Trend
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-xs">
                        Last 7 days · {metrics?.recentSignups7d ?? 0} new
                      </Text>
                    </View>
                  </View>
                  <AnimatedPressable
                    onPress={() => {
                      hapticLight();
                      router.push('/(admin)/users');
                    }}
                    className="flex-row items-center gap-1"
                  >
                    <Text style={{ color: colors.primary[600] }} className="text-xs font-semibold">
                      View all
                    </Text>
                    <ArrowUpRight size={14} color={colors.primary[600]} />
                  </AnimatedPressable>
                </View>
                <MiniBarChart
                  data={(metrics?.signupTrend ?? []).map((d) => ({ label: d.date, value: d.count }))}
                  color={colors.primary[600]}
                  height={100}
                />
              </WebCard>

              {/* Feedback by Status */}
              {canSeeFeedback && (
                <WebCard>
                  <View className="flex-row items-center justify-between mb-4">
                    <View className="flex-row items-center gap-3">
                      <View
                        className="w-10 h-10 rounded-xl items-center justify-center"
                        style={{ backgroundColor: '#FF950012' }}
                      >
                        <BarChart3 size={20} color="#FF9500" />
                      </View>
                      <View>
                        <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                          Feedback by Status
                        </Text>
                        <Text style={{ color: colors.text.secondary }} className="text-xs">
                          {metrics?.totalFeedback ?? 0} total
                        </Text>
                      </View>
                    </View>
                    <AnimatedPressable
                      onPress={() => {
                        hapticLight();
                        router.push('/(admin)/feedback');
                      }}
                      className="flex-row items-center gap-1"
                    >
                      <Text style={{ color: colors.primary[600] }} className="text-xs font-semibold">
                        View all
                      </Text>
                      <ArrowUpRight size={14} color={colors.primary[600]} />
                    </AnimatedPressable>
                  </View>

                  <View className="gap-3">
                    {(Object.entries(feedbackByStatus) as [FeedbackStatus, number][]).map(
                      ([status, count]) => {
                        const pct = maxStatusCount > 0 ? (count / maxStatusCount) * 100 : 0;
                        return (
                          <View key={status}>
                            <View className="flex-row items-center justify-between mb-1.5">
                              <View className="flex-row items-center gap-2">
                                <View
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{ backgroundColor: STATUS_COLORS[status] }}
                                />
                                <Text style={{ color: colors.text.secondary }} className="text-sm font-medium">
                                  {STATUS_LABELS[status]}
                                </Text>
                              </View>
                              <Text style={{ color: colors.text.primary }} className="text-sm font-bold">
                                {count}
                              </Text>
                            </View>
                            <View
                              className="h-2 rounded-full overflow-hidden"
                              style={{ backgroundColor: colors.surface.secondary }}
                            >
                              <Animated.View
                                entering={FadeIn.delay(400).duration(800)}
                                className="h-full rounded-full"
                                style={{
                                  backgroundColor: STATUS_COLORS[status],
                                  width: `${pct}%`,
                                }}
                              />
                            </View>
                          </View>
                        );
                      }
                    )}
                  </View>
                </WebCard>
              )}
            </WebGrid>
          </Animated.View>

          {/* Quick Actions + Activity Log row */}
          {(canSeeFeedback || canSeeActivityLog) && (
            <Animated.View entering={FadeInDown.delay(400).duration(500)} className="mb-6">
              <WebGrid columns={{ mobile: 1, tablet: 1, desktop: 2 }} gap={16}>
                {/* Quick Actions */}
                {canSeeFeedback && (
                  <WebCard>
                    <Text style={{ color: colors.text.primary }} className="text-base font-bold mb-3">
                      Quick Actions
                    </Text>
                    <QuickActions
                      items={[
                        {
                          label: 'Unresolved Feedback',
                          count: metrics?.openFeedbackCount ?? 0,
                          icon: <MessageSquare size={16} color="#FF9500" />,
                          onPress: () => {
                            hapticLight();
                            router.push('/(admin)/feedback');
                          },
                        },
                        {
                          label: 'Pending Onboarding',
                          count: metrics?.pendingOnboarding ?? 0,
                          icon: <Clock size={16} color={colors.primary[600]} />,
                          onPress: () => {
                            hapticLight();
                            router.push('/(admin)/users');
                          },
                        },
                      ]}
                    />
                  </WebCard>
                )}

                {/* Activity Log (super_admin only) */}
                {canSeeActivityLog && (
                  <WebCard>
                    <Text style={{ color: colors.text.primary }} className="text-base font-bold mb-3">
                      Admin Activity
                    </Text>
                    <AdminActivityLog entries={activityLog ?? []} />
                  </WebCard>
                )}
              </WebGrid>
            </Animated.View>
          )}

          {/* Engagement Section (analyst/super_admin) */}
          {canSeeEngagement && (
            <Animated.View entering={FadeInDown.delay(500).duration(500)}>
              <WebCard>
                <Text style={{ color: colors.text.primary }} className="text-base font-bold mb-4">
                  Engagement Overview
                </Text>
                <WebGrid columns={{ mobile: 2, tablet: 3, desktop: 5 }} gap={12}>
                  <MetricPill label="DAU" value={metrics?.dau ?? 0} color={colors.primary[600]} colors={colors} />
                  <MetricPill label="WAU" value={metrics?.wau ?? 0} color="#007AFF" colors={colors} />
                  <MetricPill label="MAU" value={metrics?.mau ?? 0} color="#34C759" colors={colors} />
                  <MetricPill label="Messages" value={metrics?.messageVolume ?? 0} color="#FF9500" colors={colors} />
                  <MetricPill label="Data Room" value={metrics?.dataRoomActivity ?? 0} color={colors.primary[600]} colors={colors} />
                </WebGrid>
              </WebCard>
            </Animated.View>
          )}
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile Dashboard
// ─────────────────────────────────────────────────────────────────────────────

interface MobileDashboardProps {
  adminRole: AdminSubRole | null | undefined;
  metrics: AdminDashboardMetricsV2 | null | undefined;
  activityLog: AdminActivityLogEntry[] | null | undefined;
  canSeeFeedback: boolean;
  canSeeEngagement: boolean;
  canSeeActivityLog: boolean;
  isSupport: boolean;
  isAnalyst: boolean;
  feedbackByStatus: Record<FeedbackStatus, number>;
  waitlistStats?: WaitlistStats | null;
  router: ReturnType<typeof useRouter>;
  colors: ReturnType<typeof useThemeColors>;
}

function MobileDashboard({
  adminRole,
  metrics,
  activityLog,
  canSeeFeedback,
  canSeeEngagement,
  canSeeActivityLog,
  isSupport,
  isAnalyst,
  feedbackByStatus,
  waitlistStats,
  router,
  colors,
}: MobileDashboardProps) {
  const [activityExpanded, setActivityExpanded] = useState<boolean>(true);
  const [engagementExpanded, setEngagementExpanded] = useState<boolean>(false);

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={['top', 'bottom']}>
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        // 100 clears the 85pt tab bar. It was 40, which left the last card
        // partly under the bar — invisible while the final section was a
        // collapsed accordion, obvious now that Tools ends the scroll.
        // Matches (investor) at 100; (startup) uses 120 for its taller CTA.
        contentContainerStyle={{ paddingBottom: 100 }}
      >
        {/* ── Zone 1: Header + KPI Strip ── */}
        <Animated.View entering={FadeInDown.delay(50).duration(400)}>
          {/* Compact header */}
          <View
            className="flex-row items-center px-4 pt-4 pb-3"
            style={{ backgroundColor: colors.surface.primary, borderBottomWidth: 1, borderBottomColor: colors.surface.secondary }}
          >
            <View
              className="w-8 h-8 rounded-xl items-center justify-center mr-2.5"
              style={{ backgroundColor: `${colors.primary[600]}12` }}
            >
              <Shield size={16} color={colors.primary[600]} />
            </View>
            <Text style={{ color: colors.text.primary }} className="text-lg font-bold flex-1">
              Admin Dashboard
            </Text>
            {adminRole && (
              <View
                className="px-2.5 py-1 rounded-full"
                style={{ backgroundColor: `${colors.primary[600]}14` }}
              >
                <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
                  {ADMIN_ROLE_LABELS[adminRole]}
                </Text>
              </View>
            )}
          </View>

          {/* KPI pill strip */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            snapToInterval={97}
            decelerationRate="fast"
            contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 12, gap: 8 }}
            style={{ flexGrow: 0 }}
          >
            {/* Primary 4 */}
            <KpiPill
              icon={<Users2 size={16} color={colors.primary[600]} />}
              iconColor={colors.primary[600]}
              value={metrics?.totalUsers ?? 0}
              label="Users"
              delta={metrics?.usersDelta}
              colors={colors}
            />
            <KpiPill
              icon={<Rocket size={16} color="#34C759" />}
              iconColor="#34C759"
              value={metrics?.totalStartups ?? 0}
              label="Startups"
              delta={metrics?.startupsDelta}
              colors={colors}
            />
            <KpiPill
              icon={<TrendingUp size={16} color="#007AFF" />}
              iconColor="#007AFF"
              value={metrics?.totalInvestors ?? 0}
              label="Investors"
              delta={metrics?.investorsDelta}
              colors={colors}
            />
            <KpiPill
              icon={<MessageSquare size={16} color="#FF9500" />}
              iconColor="#FF9500"
              value={metrics?.totalFeedback ?? 0}
              label="Feedback"
              delta={metrics?.feedbackDelta}
              onPress={() => {
                hapticLight();
                router.push('/(admin)/feedback');
              }}
              colors={colors}
            />
            {/* Role-gated secondary pills */}
            {isSupport && (
              <>
                <KpiPill
                  icon={<AlertCircle size={16} color="#FF9500" />}
                  iconColor="#FF9500"
                  value={metrics?.openFeedbackCount ?? 0}
                  label="Open"
                  onPress={() => {
                    hapticLight();
                    router.push('/(admin)/feedback');
                  }}
                  colors={colors}
                />
                <KpiPill
                  icon={<Bug size={16} color="#FF3B30" />}
                  iconColor="#FF3B30"
                  value={metrics?.criticalBugs ?? 0}
                  label="Bugs"
                  colors={colors}
                />
              </>
            )}
            {isAnalyst && (
              <>
                <KpiPill
                  icon={<BarChart3 size={16} color={colors.primary[600]} />}
                  iconColor={colors.primary[600]}
                  value={metrics?.avgScore ?? 0}
                  label="Avg Score"
                  colors={colors}
                />
                <KpiPill
                  icon={<Zap size={16} color="#34C759" />}
                  iconColor="#34C759"
                  value={
                    metrics?.dau && metrics?.totalUsers
                      ? `${Math.round((metrics.dau / metrics.totalUsers) * 100)}%`
                      : '0%'
                  }
                  label="Active"
                  colors={colors}
                />
              </>
            )}
          </ScrollView>
        </Animated.View>

        {/* ── Beta: Waitlist Stats — DELETE this block when beta ends ── */}
        {isBetaMode() && waitlistStats && (
          <Animated.View entering={FadeInDown.delay(120).duration(400)} className="mx-4 mt-3">
            <View className="rounded-2xl p-4" style={{ backgroundColor: colors.surface.primary }}>
              <View className="flex-row items-center justify-between mb-3">
                <Text style={{ color: colors.text.primary }} className="text-base font-bold" numberOfLines={1}>
                  Waitlist
                </Text>
                <View className="flex-row items-center gap-2">
                  {/* The counters above are read-only; inviting people lives on the
                      dedicated screen, which is the ONLY way anyone can create an
                      account while the beta is invite-only. */}
                  <AnimatedPressable
                    onPress={() => router.push('/(admin)/waitlist')}
                    className="px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: `${colors.primary[600]}12` }}
                  >
                    <Text style={{ color: colors.primary[600] }} className="text-xs font-semibold" numberOfLines={1}>
                      Manage
                    </Text>
                  </AnimatedPressable>
                  <View className="px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FF950015' }}>
                    <Text style={{ color: '#FF9500' }} className="text-xs font-semibold">Beta</Text>
                  </View>
                </View>
              </View>

              {/* KPI row */}
              <View className="flex-row gap-2 mb-3">
                <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: `${colors.primary[600]}08` }}>
                  <Text style={{ color: colors.text.primary }} className="text-lg font-bold">{waitlistStats.verified}</Text>
                  <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>Verified</Text>
                </View>
                <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: '#34C75908' }}>
                  <Text style={{ color: colors.text.primary }} className="text-lg font-bold">{waitlistStats.invited}</Text>
                  <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>Invited</Text>
                </View>
                <View className="flex-1 items-center py-2 rounded-xl" style={{ backgroundColor: '#007AFF08' }}>
                  <Text style={{ color: colors.text.primary }} className="text-lg font-bold">{waitlistStats.converted}</Text>
                  <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>Converted</Text>
                </View>
              </View>

              {/* Role split */}
              <View className="flex-row gap-2 mb-3">
                <View className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-full" style={{ backgroundColor: '#0D948808' }}>
                  <Rocket size={12} color="#0D9488" />
                  <Text style={{ color: colors.text.secondary }} className="text-xs font-medium" numberOfLines={1}>
                    {waitlistStats.startups} startups
                  </Text>
                </View>
                <View className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-full" style={{ backgroundColor: '#3B82F608' }}>
                  <TrendingUp size={12} color="#3B82F6" />
                  <Text style={{ color: colors.text.secondary }} className="text-xs font-medium" numberOfLines={1}>
                    {waitlistStats.investors} investors
                  </Text>
                </View>
                <View className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-full" style={{ backgroundColor: `${colors.text.tertiary}08` }}>
                  <Text style={{ color: colors.text.secondary }} className="text-xs font-medium" numberOfLines={1}>
                    {waitlistStats.undecided} undecided
                  </Text>
                </View>
              </View>

              {/* Trend chart */}
              <MiniBarChart
                data={waitlistStats.dailyTrend.map((d) => ({ label: d.date, value: d.count }))}
                color={colors.primary[600]}
                height={60}
              />

              {/* Bottom stats row */}
              <View className="flex-row items-center justify-between mt-3 pt-3" style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}>
                <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>
                  Today: <Text style={{ color: colors.text.primary }} className="font-bold">{waitlistStats.signupsToday}</Text>
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>
                  This week: <Text style={{ color: colors.text.primary }} className="font-bold">{waitlistStats.signupsThisWeek}</Text>
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-xs" numberOfLines={1}>
                  Conv: <Text style={{ color: '#34C759' }} className="font-bold">{waitlistStats.conversionRate}%</Text>
                </Text>
              </View>
            </View>
          </Animated.View>
        )}

        {/* ── Zone 2: Trend + Status ── */}
        <Animated.View entering={FadeInDown.delay(150).duration(400)} className="mx-4 mt-3">
          <View
            className="rounded-2xl overflow-hidden p-4"
            style={{ backgroundColor: colors.surface.primary }}
          >
            {/* Chart header */}
            <View className="flex-row items-center justify-between mb-3">
              <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                Signups (7d)
              </Text>
              <AnimatedPressable
                onPress={() => {
                  hapticLight();
                  router.push('/(admin)/users');
                }}
                className="flex-row items-center gap-1"
              >
                <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
                  View all
                </Text>
                <ArrowUpRight size={12} color={colors.primary[600]} />
              </AnimatedPressable>
            </View>

            {/* Bar chart */}
            <MiniBarChart
              data={(metrics?.signupTrend ?? []).map((d) => ({ label: d.date, value: d.count }))}
              color={colors.primary[600]}
              height={80}
            />

          </View>
        </Animated.View>

        {/* Feedback by Status — its OWN card.
            These pills were rendered inside the "Signups (7d)" card with only a
            comment separating them, so two unrelated datasets shared one titled
            container and the pills appeared to be part of the signup chart. */}
        <Animated.View entering={FadeInDown.delay(200).duration(400)} className="mx-4 mt-3">
          <View
            className="rounded-2xl p-4"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <Text style={{ color: colors.text.primary }} className="text-base font-bold mb-3">
              Feedback by Status
            </Text>
            <View className="flex-row flex-wrap gap-2">
              {(Object.entries(feedbackByStatus) as [FeedbackStatus, number][]).map(([status, count]) => (
                <View
                  key={status}
                  className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-full"
                  style={{ backgroundColor: `${STATUS_COLORS[status]}14` }}
                >
                  <View
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: STATUS_COLORS[status] }}
                  />
                  <Text style={{ color: colors.text.secondary }} className="text-sm font-medium" numberOfLines={1}>
                    {STATUS_LABELS[status]}
                  </Text>
                  <Text style={{ color: colors.text.primary }} className="text-xs font-bold" numberOfLines={1}>
                    {count}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </Animated.View>

        {/* ── Zone 3: Actions + Activity + Engagement ── */}
        <Animated.View entering={FadeInDown.delay(250).duration(400)} className="mx-4 mt-3 gap-3">
          {/* Quick Actions */}
          {canSeeFeedback && (
            <View
              className="rounded-2xl p-4"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <Text style={{ color: colors.text.primary }} className="text-base font-bold mb-3">
                Quick Actions
              </Text>
              <QuickActions
                items={[
                  {
                    label: 'Unresolved Feedback',
                    count: metrics?.openFeedbackCount ?? 0,
                    icon: <MessageSquare size={16} color="#FF9500" />,
                    onPress: () => {
                      hapticLight();
                      router.push('/(admin)/feedback');
                    },
                  },
                  {
                    label: 'Pending Onboarding',
                    count: metrics?.pendingOnboarding ?? 0,
                    icon: <Clock size={16} color={colors.primary[600]} />,
                    onPress: () => {
                      hapticLight();
                      router.push('/(admin)/users');
                    },
                  },
                ]}
              />
            </View>
          )}

          {/* Collapsible Activity Log */}
          {canSeeActivityLog && (
            <View
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <AnimatedPressable
                onPress={() => {
                  hapticLight();
                  setActivityExpanded((v) => !v);
                }}
                accessibilityRole="button"
                accessibilityState={{ expanded: activityExpanded }}
                accessibilityLabel={`Admin Activity, ${activityExpanded ? 'expanded' : 'collapsed'}`}
                className="flex-row items-center justify-between px-4 py-3.5"
              >
                <View className="flex-row items-center gap-2">
                  <Activity size={15} color={colors.primary[600]} />
                  <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                    Admin Activity
                  </Text>
                </View>
                <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
                  {activityExpanded ? 'Hide' : 'Show'}
                </Text>
              </AnimatedPressable>
              {activityExpanded && (
                <Animated.View entering={FadeIn.duration(200)} exiting={FadeOut.duration(150)} className="px-4 pb-4">
                  <AdminActivityLog
                    entries={(activityLog ?? []).slice(0, 3)}
                    maxHeight={240}
                  />
                  <AnimatedPressable
                    onPress={() => { hapticLight(); router.push('/(admin)/users'); }}
                    className="flex-row items-center justify-center gap-1 mt-2 pt-2"
                    style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}
                  >
                    <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">View all</Text>
                    <ArrowUpRight size={12} color={colors.primary[600]} />
                  </AnimatedPressable>
                </Animated.View>
              )}
            </View>
          )}

          {/* Collapsible Engagement */}
          {canSeeEngagement && (
            <View
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <AnimatedPressable
                onPress={() => {
                  hapticLight();
                  setEngagementExpanded((v) => !v);
                }}
                accessibilityRole="button"
                accessibilityState={{ expanded: engagementExpanded }}
                accessibilityLabel={`Engagement, ${engagementExpanded ? 'expanded' : 'collapsed'}`}
                className="flex-row items-center justify-between px-4 py-3.5"
              >
                <View className="flex-row items-center gap-2">
                  <BarChart3 size={15} color={colors.primary[600]} />
                  <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                    Engagement
                  </Text>
                </View>
                <Text style={{ color: colors.primary[600] }} className="text-sm font-semibold">
                  {engagementExpanded ? 'Hide' : 'Show'}
                </Text>
              </AnimatedPressable>
              {engagementExpanded && (
                <Animated.View entering={FadeIn.duration(200)} exiting={FadeOut.duration(150)} className="px-4 pb-4">
                  <View className="flex-row flex-wrap gap-2">
                    <MetricPill label="DAU" value={metrics?.dau ?? 0} color={colors.primary[600]} colors={colors} />
                    <MetricPill label="WAU" value={metrics?.wau ?? 0} color="#007AFF" colors={colors} />
                    <MetricPill label="MAU" value={metrics?.mau ?? 0} color="#34C759" colors={colors} />
                    <MetricPill label="Messages" value={metrics?.messageVolume ?? 0} color="#FF9500" colors={colors} />
                    <MetricPill label="Data Room" value={metrics?.dataRoomActivity ?? 0} color={colors.primary[600]} colors={colors} />
                    {isAnalyst && (
                      <>
                        <MetricPill label="Avg Score" value={metrics?.avgScore ?? 0} color={colors.primary[600]} colors={colors} />
                        <MetricPill
                          label="Active %"
                          value={
                            metrics?.dau && metrics?.totalUsers
                              ? Math.round((metrics.dau / metrics.totalUsers) * 100)
                              : 0
                          }
                          color="#34C759"
                          colors={colors}
                        />
                      </>
                    )}
                  </View>
                </Animated.View>
              )}
            </View>
          )}
          {/* ---------------------------------------------------------------
              TOOLS — the screens that are no longer tabs.

              The bar carried ten tabs against an app convention of four to
              five, which on a 375pt screen left ~37pt per tab (under the 44pt
              minimum) with 10pt labels crushed under the icons. Moving the
              five secondary screens here keeps every one of them one tap from
              the dashboard while the bar returns to the shape the rest of the
              app uses. On web the sidebar (ADMIN_NAV) still lists them all, so
              this section is mobile-only by virtue of living in MobileDashboard.
              --------------------------------------------------------------- */}
          <View
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <View className="px-4 pt-3.5 pb-1">
              <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                Tools
              </Text>
            </View>
            {ADMIN_TOOLS.map((tool, i) => (
              <AnimatedPressable
                key={tool.path}
                onPress={() => { hapticLight(); router.push(tool.path as never); }}
                accessibilityRole="button"
                accessibilityLabel={tool.label}
                accessibilityHint={tool.hint}
                className="flex-row items-center gap-3 px-4"
                // 52pt row: comfortably above the 44pt minimum target, and the
                // whole row is the target rather than just the label.
                style={{
                  minHeight: 52,
                  borderTopWidth: i === 0 ? 0 : 1,
                  borderTopColor: colors.border.light,
                }}
              >
                <View
                  className="w-8 h-8 rounded-xl items-center justify-center"
                  style={{ backgroundColor: colors.primary[600] + '14' }}
                >
                  <tool.icon size={15} color={colors.primary[600]} />
                </View>
                <View className="flex-1">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-sm font-semibold"
                    numberOfLines={1}
                  >
                    {tool.label}
                  </Text>
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-xs"
                    numberOfLines={2}
                  >
                    {tool.hint}
                  </Text>
                </View>
                <ChevronRight size={16} color={colors.text.secondary} />
              </AnimatedPressable>
            ))}
          </View>
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
}

/**
 * The screens removed from the tab bar. Kept beside the section that renders
 * them so the two cannot drift — if a screen is added back to the bar it must
 * leave this list, and vice versa.
 */
const ADMIN_TOOLS = [
  { label: 'Product Analytics', hint: 'Funnels, retention, feature usage', icon: Activity, path: '/(admin)/product-analytics' },
  { label: 'Feature Flags', hint: 'Toggle features per environment', icon: Flag, path: '/(admin)/feature-flags' },
  { label: 'Data Retention', hint: 'Retention windows and purge runs', icon: Timer, path: '/(admin)/data-retention' },
  { label: 'Erasure Requests', hint: 'Data-subject requests from privacy@', icon: UserX, path: '/(admin)/erasure-requests' },
  { label: 'AI Cost', hint: 'Model spend and usage', icon: DollarSign, path: '/(admin)/ai-cost' },
  { label: 'AI Quality', hint: 'Evaluations and observability', icon: Gauge, path: '/(admin)/ai-quality' },
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// KpiPill
// ─────────────────────────────────────────────────────────────────────────────

function KpiPill({
  icon,
  iconColor,
  value,
  label,
  delta,
  onPress,
  colors,
}: {
  icon: React.ReactNode;
  iconColor: string;
  value: number | string;
  label: string;
  delta?: number;
  onPress?: () => void;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const content = (
    <View
      className="items-center rounded-2xl px-3 py-3"
      style={{ backgroundColor: colors.surface.primary, width: 90 }}
    >
      <View
        className="w-8 h-8 rounded-xl items-center justify-center mb-1.5"
        style={{ backgroundColor: `${iconColor}12` }}
      >
        {icon}
      </View>
      <Text
        style={{ color: colors.text.primary }}
        className="text-lg font-bold"
        numberOfLines={1}
      >
        {value}
      </Text>
      <Text
        style={{ color: colors.text.secondary }}
        className="text-sm mt-0.5"
        numberOfLines={1}
      >
        {label}
      </Text>
      {delta != null && (
        <View className="flex-row items-center mt-1">
          <Text
            style={{ color: delta >= 0 ? '#34C759' : '#FF3B30' }}
            className="text-xs font-semibold"
          >
            {delta >= 0 ? '↑' : '↓'}{Math.abs(delta)}%
          </Text>
        </View>
      )}
    </View>
  );
  if (onPress) return <AnimatedPressable onPress={onPress} hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }} accessibilityRole="button">{content}</AnimatedPressable>;
  return content;
}

function MetricPill({
  label,
  value,
  color,
  colors,
}: {
  label: string;
  value: number;
  color: string;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View
      className="rounded-xl p-3 items-center"
      style={{ backgroundColor: `${color}08` }}
    >
      <Text style={{ color: colors.text.primary }} className="text-xl font-bold" numberOfLines={1}>
        {value}
      </Text>
      <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mt-0.5" numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
}
